import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReplayIcon from '@mui/icons-material/Replay';
import BlockIcon from '@mui/icons-material/Block';
import { PageContainer, Section } from '../../components/AdminContentWrapper';
import { DataContext } from '../../../../context/DataContext';
import {
  listInvites,
  createInvite,
  resendInvite,
  revokeInvite
} from '../../../../services/UserInviteService';
import TablePaginationBar from '../../../../components/common/TablePaginationBar';
import { useTablePagination } from '../../../../hooks';
import HelpIcon from '../../../../components/help/HelpIcon';

const ROLE_OPTIONS = [
  { value: 'analyst', label: 'Analista' },
  { value: 'viewer', label: 'Viewer' },
  { value: 'admin', label: 'Administrador' }
];

const STATUS_OPTIONS = [
  { value: 'ALL', label: 'Todos' },
  { value: 'PENDING', label: 'Pendientes' },
  { value: 'ACCEPTED', label: 'Aceptadas' },
  { value: 'EXPIRED', label: 'Expiradas' },
  { value: 'REVOKED', label: 'Revocadas' }
];

const STATUS_META = {
  PENDING: { label: 'Pendiente', color: 'var(--wb-accent)', background: 'var(--wb-x-bg-0ea5e92e, rgba(14, 165, 233, 0.18))' },
  ACCEPTED: { label: 'Aceptada', color: 'var(--wb-success)', background: 'var(--wb-x-bg-4ade8033, rgba(74, 222, 128, 0.2))' },
  EXPIRED: { label: 'Expirada', color: 'var(--wb-warning)', background: 'var(--wb-x-bg-fbbf242e, rgba(251, 191, 36, 0.18))' },
  REVOKED: { label: 'Revocada', color: 'var(--wb-danger)', background: 'var(--wb-x-bg-f8717133, rgba(248, 113, 113, 0.2))' }
};

const defaultFormState = {
  email: '',
  role: ROLE_OPTIONS[0].value,
  expiresAt: ''
};

const formatDateTime = (value) => {
  if (!value) {
    return 'Sin caducidad';
  }

  const dateCandidate = typeof value === 'number'
    ? new Date(value * 1000)
    : new Date(value);

  if (Number.isNaN(dateCandidate.getTime())) {
    return 'Sin caducidad';
  }

  return new Intl.DateTimeFormat('es-MX', {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(dateCandidate);
};

const UserInvitesPage = () => {
  // isPlatformRoot/isTenantAdmin vienen de los grupos de Cognito "root"/
  // "admin" (ver DataContext.js). root gestiona invitaciones de cualquier
  // tenant; admin gestiona las de su propio tenant (el scoping ya lo da el
  // backend vía account_id del JWT, no hace falta filtrarlo acá).
  // accountId ya viene resuelto por el contexto, con el mismo fallback a
  // localStorage que usa el resto de la app.
  const { isPlatformRoot, isTenantAdmin, accountId, userData } = useContext(DataContext);
  const canManageInvites = isPlatformRoot || isTenantAdmin;

  // El contexto no expone un flag explícito de "cargando" -- se usa
  // `userData` (se setea una sola vez, al terminar el fetch inicial de
  // sesión) como proxy para no mostrar "acceso restringido" antes de saber
  // el rol real del usuario.
  const accessChecked = Boolean(userData);

  const [formState, setFormState] = useState(defaultFormState);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [invites, setInvites] = useState([]);
  const [actionLoading, setActionLoading] = useState({ id: null, type: null });

  const [cursor, setCursor] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const fetchInvites = useCallback(async () => {
    if (!canManageInvites || !accountId) {
      return;
    }

    setListLoading(true);
    setListError('');
    try {
      const { items, nextCursor } = await listInvites({
        status: statusFilter === 'ALL' ? undefined : statusFilter
      });
      setInvites(items);
      setCursor(nextCursor);
    } catch (error) {
      setListError(error?.message || 'No se pudo obtener el listado de invitaciones.');
    } finally {
      setListLoading(false);
    }
  }, [canManageInvites, accountId, statusFilter]);

  // El backend pagina con cursor: esto trae la siguiente tanda cuando el
  // usuario llega al final de lo ya cargado.
  const fetchMoreInvites = useCallback(async () => {
    if (!canManageInvites || !accountId || !cursor || isLoadingMore) {
      return;
    }
    setIsLoadingMore(true);
    try {
      const { items, nextCursor } = await listInvites({
        status: statusFilter === 'ALL' ? undefined : statusFilter,
        cursor
      });
      setInvites((prev) => [...prev, ...items]);
      setCursor(nextCursor);
    } catch (error) {
      setListError(error?.message || 'No se pudieron obtener más invitaciones.');
    } finally {
      setIsLoadingMore(false);
    }
  }, [canManageInvites, accountId, statusFilter, cursor, isLoadingMore]);

  // Declarado antes del early return de `accessChecked`: un hook después
  // de un return condicional rompe el orden de hooks de React.
  const { pageRows, paginationProps } = useTablePagination(invites, {
    storageKey: 'userInvites',
    resetDeps: [statusFilter],
    hasMore: Boolean(cursor),
    isLoadingMore,
    onNeedMore: fetchMoreInvites
  });

  useEffect(() => {
    fetchInvites();
  }, [fetchInvites]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const canSubmit = useMemo(() => {
    if (!formState.email.trim()) {
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(formState.email.trim())) {
      return false;
    }
    return Boolean(formState.role);
  }, [formState]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canManageInvites || !accountId) {
      setFeedback({
        severity: 'warning',
        message: 'Solo administradores de la cuenta pueden enviar invitaciones.'
      });
      return;
    }

    setIsSubmitting(true);
    setFeedback(null);
    try {
      await createInvite({
        email: formState.email,
        role: formState.role,
        expiresAt: formState.expiresAt || undefined
      });
      setFeedback({
        severity: 'success',
        message: `Se envió la invitación a ${formState.email}.`
      });
      setFormState((prev) => ({
        ...defaultFormState,
        role: prev.role
      }));
      await fetchInvites();
    } catch (error) {
      setFeedback({
        severity: 'error',
        message: error?.message || 'No se pudo enviar la invitación.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };

  const handleResend = async (inviteId) => {
    setActionLoading({ id: inviteId, type: 'resend' });
    setFeedback(null);
    try {
      await resendInvite(inviteId);
      setFeedback({
        severity: 'success',
        message: 'La invitación fue reenviada exitosamente.'
      });
      await fetchInvites();
    } catch (error) {
      setFeedback({
        severity: 'error',
        message: error?.message || 'No fue posible reenviar la invitación.'
      });
    } finally {
      setActionLoading({ id: null, type: null });
    }
  };

  const handleRevoke = async (inviteId) => {
    setActionLoading({ id: inviteId, type: 'revoke' });
    setFeedback(null);
    try {
      await revokeInvite(inviteId);
      setFeedback({
        severity: 'success',
        message: 'La invitación fue revocada correctamente.'
      });
      await fetchInvites();
    } catch (error) {
      setFeedback({
        severity: 'error',
        message: error?.message || 'No fue posible revocar la invitación.'
      });
    } finally {
      setActionLoading({ id: null, type: null });
    }
  };

  const handleRefresh = () => {
    fetchInvites();
  };

  const renderStatusChip = (status) => {
    const meta = STATUS_META[status] || {
      label: status,
      color: 'var(--wb-text-label)',
      background: 'var(--wb-border-16)'
    };
    return (
      <Chip
        label={meta.label}
        size="small"
        sx={{
          color: meta.color,
          backgroundColor: meta.background,
          fontWeight: 600
        }}
      />
    );
  };

  if (!accessChecked) {
    return (
      <PageContainer title="Invitar usuarios" description="Gestiona la invitación y enrolamiento de usuarios para tu organización.">
        <Section title="Cargando acceso">
          <Stack direction="row" spacing={2} alignItems="center">
            <CircularProgress size={24} />
            <Typography sx={{ color: 'var(--wb-text-label)' }}>
              Verificando permisos...
            </Typography>
          </Stack>
        </Section>
      </PageContainer>
    );
  }

  if (!canManageInvites || !accountId) {
    return (
      <PageContainer
        title="Invitar usuarios"
        description="Solo administradores de la cuenta pueden gestionar las invitaciones."
      >
        <Section title="Acceso restringido">
          <Alert severity="warning" sx={{ backgroundColor: 'var(--wb-warning-12)', color: 'var(--wb-warning)' }}>
            Necesitas privilegios de administrador (de tu cuenta o de la plataforma) para acceder a esta sección. Contacta al administrador de la cuenta si crees que se trata de un error.
          </Alert>
        </Section>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      title="Invitar usuarios"
      description="Envía, reenvía y revoca invitaciones para nuevos miembros de la organización."
      actions={[
        <Button
          key="refresh"
          variant="outlined"
          color="info"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={listLoading}
          sx={{ textTransform: 'none', borderColor: 'var(--wb-accent-40)', color: 'var(--wb-accent)' }}
        >
          Actualizar
        </Button>
      ]}
    >
      {feedback && (
        <Box sx={{ mb: 3 }}>
          <Alert
            severity={feedback.severity}
            onClose={() => setFeedback(null)}
            sx={{ backgroundColor: 'var(--wb-x-bg-0e749026, rgba(14, 116, 144, 0.15))', color: 'var(--wb-text)' }}
          >
            {feedback.message}
          </Alert>
        </Box>
      )}

      <Section
        title="Enviar nueva invitación"
        subtitle="Comparte acceso a Whisper BI asignando el rol y la caducidad deseada."
      >
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr 120px' } }}
        >
          <TextField
            name="email"
            label="Correo electrónico"
            type="email"
            required
            value={formState.email}
            onChange={handleInputChange}
            placeholder="usuario@empresa.com"
            autoComplete="off"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <HelpIcon module="seguridad" topic="invitaciones" anchor="correo-electrónico" />
                </InputAdornment>
              )
            }}
            sx={{
              '& .MuiInputBase-root': {
                backgroundColor: 'var(--wb-bg-surface)',
                color: 'var(--wb-text-title)'
              }
            }}
          />
          <Stack direction="row" alignItems="flex-start" spacing={0.5}>
            <FormControl
              sx={{
                '& .MuiInputBase-root': {
                  backgroundColor: 'var(--wb-bg-surface)',
                  color: 'var(--wb-text-title)'
                }
              }}
            >
              <InputLabel>Rol</InputLabel>
              <Select
                name="role"
                label="Rol"
                value={formState.role}
                onChange={handleInputChange}
              >
                {ROLE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ mt: 1 }}>
              <HelpIcon module="seguridad" topic="invitaciones" anchor="rol" />
            </Box>
          </Stack>
          <TextField
            name="expiresAt"
            label="Expira (opcional)"
            type="datetime-local"
            value={formState.expiresAt}
            onChange={handleInputChange}
            helperText="La invitación expira cuando se cumpla la fecha."
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <HelpIcon module="seguridad" topic="invitaciones" anchor="expira-opcional" />
                </InputAdornment>
              )
            }}
            sx={{
              '& .MuiInputBase-root': {
                backgroundColor: 'var(--wb-bg-surface)',
                color: 'var(--wb-text-title)'
              },
              '& .MuiFormHelperText-root': {
                color: 'var(--wb-muted-80)'
              }
            }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={!canSubmit || isSubmitting}
              sx={{ width: '100%', textTransform: 'none', fontWeight: 600 }}
            >
              {isSubmitting ? 'Enviando...' : 'Invitar'}
            </Button>
          </Box>
        </Box>
      </Section>

      <Section
        title="Invitaciones enviadas"
        subtitle="Controla el estado de cada invitación y actúa rápidamente ante expiraciones o reenvíos."
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'center' }}
          sx={{ mb: 2 }}
        >
          <Typography sx={{ color: 'var(--wb-muted-80)' }}>
            Cuenta: <strong>{accountId}</strong>
          </Typography>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <FormControl
              size="small"
              sx={{
                minWidth: 180,
                '& .MuiInputBase-root': {
                  backgroundColor: 'var(--wb-bg-surface)',
                  color: 'var(--wb-text-title)'
                }
              }}
            >
              <InputLabel>Filtrar por estado</InputLabel>
              <Select
                value={statusFilter}
                label="Filtrar por estado"
                onChange={handleStatusFilterChange}
              >
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <HelpIcon module="seguridad" topic="invitaciones" anchor="filtrar-por-estado" />
          </Stack>
        </Stack>

        {listError && (
          <Alert
            severity="error"
            sx={{ mb: 2, backgroundColor: 'var(--wb-danger-18)', color: 'var(--wb-danger-soft)' }}
          >
            {listError}
          </Alert>
        )}

        <Box sx={{ position: 'relative', overflowX: 'auto' }}>
          <Table
            size="small"
            sx={{
              minWidth: 960,
              '& th': { color: 'var(--wb-x-text-94a3b8e6, rgba(148, 163, 184, 0.9))', fontWeight: 600, borderBottomColor: 'var(--wb-border)' },
              '& td': { color: 'var(--wb-text)', borderBottomColor: 'var(--wb-border-06)' }
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Correo</TableCell>
                <TableCell>Rol</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Expira</TableCell>
                <TableCell>Invitado por</TableCell>
                <TableCell>Enlace</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invites.length === 0 && !listLoading && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography sx={{ color: 'var(--wb-muted-70)', textAlign: 'center', py: 3 }}>
                      No hay invitaciones para mostrar con el filtro seleccionado.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {pageRows.map((invite) => {
                const { invite_id: inviteId } = invite;
                const disableActions = invite.status !== 'PENDING';
                const loadingCurrent = actionLoading.id === inviteId;

                return (
                  <TableRow key={inviteId}>
                    <TableCell>{invite.email}</TableCell>
                    <TableCell>{invite.role}</TableCell>
                    <TableCell>{renderStatusChip(invite.status)}</TableCell>
                    <TableCell>{formatDateTime(invite.expires_at_iso || invite.expires_at)}</TableCell>
                    <TableCell>{invite.inviter_name || '—'}</TableCell>
                    <TableCell>
                      {invite.invite_link ? (
                        <Typography
                          component="a"
                          href={invite.invite_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ color: 'var(--wb-accent)', textDecoration: 'none' }}
                        >
                          Abrir enlace
                        </Typography>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Tooltip title="Reenviar invitación">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleResend(inviteId)}
                              disabled={disableActions || loadingCurrent}
                              sx={{ color: 'var(--wb-accent)' }}
                            >
                              {loadingCurrent && actionLoading.type === 'resend'
                                ? <CircularProgress size={16} />
                                : <ReplayIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Revocar invitación">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleRevoke(inviteId)}
                              disabled={disableActions || loadingCurrent}
                              sx={{ color: 'var(--wb-danger)' }}
                            >
                              {loadingCurrent && actionLoading.type === 'revoke'
                                ? <CircularProgress size={16} />
                                : <BlockIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          {invites.length > 0 && <TablePaginationBar {...paginationProps} />}

          {listLoading && (
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="center"
              sx={{
                position: 'absolute',
                inset: 0,
                backgroundColor: 'var(--wb-bg-overlay-70)'
              }}
            >
              <CircularProgress size={24} />
              <Typography sx={{ color: 'var(--wb-text-label)' }}>Actualizando invitaciones...</Typography>
            </Stack>
          )}
        </Box>
      </Section>
    </PageContainer>
  );
};

export default UserInvitesPage;
